import axios, { CancelTokenSource } from "axios";
import AddButton from "components/AddButton";
import UpsertBlockProcedure from "components/blockprocedure/UpsertBlockProcedure";
import DeleteActionButton from "components/DeleteActionButton";
import EditActionButton from "components/EditActionButton";
import { mapToDropdownOptionArray } from "components/final-form/dropdownUtils";
import Input from "components/final-form/Input";
import Select from "components/final-form/Select";
import PraxStyledSegment from "components/layout/PraxStyledSegment";
import PxGrid from "components/layout/PxGrid";
import StyledViewTableRow from "components/layout/StyledViewTableRow";
import VirtualizedTable from "components/layout/VirtualizedTable";
import SearchResultComponent from "components/SearchResultComponent";
import { FormState } from "final-form";
import React, { Component } from "react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { deleteBlockProcedure, searchBlockProcedures } from "services/blockProcedureServices";
import { clearProceduresSearchCache } from "services/searchProceduresServices";
import { getAllTariffs } from "services/tariffServices";
import { debounce } from "ts-debounce";
import { BlockProcedureDto, BlockProcedureSearchRequest, TariffDto } from "ts-types/api.types";
import { noop } from "utils/functionUtils";

interface Props extends MessageSourceProps {

}

const initialBlockProcedureSearch: Partial<BlockProcedureSearchRequest> = {};

interface BlockProcedureFieldProps extends FormState {
  values: Partial<BlockProcedureSearchRequest>;
}

interface State {
  blockProcedureId?: number;
  tariffs: Array<TariffDto>;
  tariffId: number;
  foundBlockProcedures: Array<BlockProcedureDto>;
  loadingProcedureBlockData: boolean;
  upsertBlockProcedureModalOpen: boolean;
  searchId?: string;
  cancelTokenSource: CancelTokenSource;
  searchValues: Partial<BlockProcedureSearchRequest>;
}

class BlockProceduresView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      tariffs: [],
      tariffId: -1,
      foundBlockProcedures: [],
      loadingProcedureBlockData: true,
      upsertBlockProcedureModalOpen: false,
      cancelTokenSource: cancelTokenSource,
      searchValues: initialBlockProcedureSearch
    };

    const onFinally = () => {
      this.setState({
        loadingProcedureBlockData: false
      });
    };

    getAllTariffs(cancelTokenSource)
      .then(response => {
        const tariffId = response[0].lnkTrfId != null ? response[0].lnkTrfId : response[0].id;
        this.setState({
          tariffs: response,
          tariffId: tariffId
        });
      }).catch(noop)
      .finally(onFinally);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const { cancelTokenSource } = this.state;
    cancelTokenSource.cancel("Operation canceled by user");
  }

  setCacheSearchId = (searchId?: string): void => {
    this.setState({
      searchId
    });
  };

  handleProcedureBlockSearchChange = debounce(({ values }: BlockProcedureFieldProps) => {

    if (!this.mounted) {
      return;
    }

    const { cancelTokenSource, tariffs } = this.state;

    let valuesCopy = { ...values, selection: 1 };
    if (values.tariffId != null) {
      this.setState({ loadingProcedureBlockData: true });

      let tariffId = values.tariffId;

      const onFinally = () => {
        this.setState({
          loadingProcedureBlockData: false
        });
      };

      valuesCopy = {
        ...valuesCopy,
        tariffId: tariffId
      };

      searchBlockProcedures(valuesCopy, cancelTokenSource)
        .then(response =>
          this.setState({
            foundBlockProcedures: response,
            tariffId: tariffId,
            searchValues: values
          })
        )
        .catch(noop)
        .finally(onFinally);
    }
  }, 300);

  onOpenUpsertBlockProcedureModal = () => {
    this.setState(() => ({
      upsertBlockProcedureModalOpen: true
    }));
  };

  onCloseUpsertBlockProcedureModal = () => {
    const { searchId, cancelTokenSource } = this.state;

    if (searchId) {
      clearProceduresSearchCache(searchId, cancelTokenSource)
        .then(response => response)
        .catch(noop);
    }

    this.setState(() => ({
      searchId: undefined,
      upsertBlockProcedureModalOpen: false
    }));
  };

  upsertBlockProcedure = (blockProcedureId?: number) => {
    this.setState({
      blockProcedureId: blockProcedureId
    });

    this.onOpenUpsertBlockProcedureModal();
  };

  onUpsertBlockProcedure = (blockProcedureDto: BlockProcedureDto) => {

    const { foundBlockProcedures: blockProcedures } = this.state;

    const foundBlockProcedureIndex = blockProcedures
      .findIndex(blockProcedure => blockProcedure.id == blockProcedureDto.id);

    if (foundBlockProcedureIndex != -1) {
      const modifiedBlockProcedures = Array.from(blockProcedures);

      modifiedBlockProcedures[foundBlockProcedureIndex] = {
        ...blockProcedureDto
      };

      this.setState({
        foundBlockProcedures: modifiedBlockProcedures
      });
    } else {
      this.setState({
        foundBlockProcedures: [...blockProcedures, blockProcedureDto]
      });
    }
  };

  onDeleteBlockProcedure = (blockProcedureDto: BlockProcedureDto) => {

    const { foundBlockProcedures, cancelTokenSource } = this.state;

    deleteBlockProcedure(blockProcedureDto.id, cancelTokenSource)
      .then(() => {
        const modifiedBlockProcedures = foundBlockProcedures
          .filter(blockProcedure => blockProcedure.id != blockProcedureDto.id);

        this.setState({
          foundBlockProcedures: modifiedBlockProcedures
        });
      }).catch(noop);
  };

  render(): React.ReactNode {

    const {
      tariffs,
      tariffId,
      loadingProcedureBlockData,
      blockProcedureId,
      upsertBlockProcedureModalOpen
    } = this.state;

    const foundTariff = tariffs.find(tariff => tariff.id == tariffId);

    return (
      <PraxStyledSegment>
        <div>
          <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              tariffId: tariffs && tariffs.length ? tariffs[0].id : undefined
            }}
            subscription={{ pristine: true }}
            render={this.renderSearchForm}
          />
          {
            upsertBlockProcedureModalOpen &&
            <UpsertBlockProcedure
              modalOpen={upsertBlockProcedureModalOpen}
              onCloseModal={this.onCloseUpsertBlockProcedureModal}
              onUpsertBlockProcedure={this.onUpsertBlockProcedure}
              setCacheSearchId={this.setCacheSearchId}
              blockProcedureId={blockProcedureId}
              tariffId={tariffId}
              tarmedTariff={foundTariff ? foundTariff.tarmedTariff : false}
            />
          }
        </div>

        <StyledViewTableRow>
          {loadingProcedureBlockData ? this.renderLoader() : this.renderBlockProceduresSegment()}
        </StyledViewTableRow>

      </PraxStyledSegment>
    );
  }

  renderSearchForm = (): React.ReactNode => {

    const { getMessage } = this.props;
    const { tariffs, foundBlockProcedures } = this.state;

    const numResults = foundBlockProcedures.length;

    const mappedTariffs = mapToDropdownOptionArray(tariffs, "description");

    return (
      <PxGrid>
        <Grid.Row>
          <Grid.Column width={3} verticalAlign="middle">
            {getMessage("tariff")}:
          </Grid.Column>
          <Grid.Column width={4}>
            <Field
              name="tariffId"
              component={Select}
              fluid
              options={mappedTariffs}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3} verticalAlign="middle">
            {getMessage("description")}:
          </Grid.Column>
          <Grid.Column width={13}>
            <Field
              name="description"
              component={Input}
              fluid
              autoFocus
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.upsertBlockProcedure()} />
          </Grid.Column>
        </Grid.Row>
        <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"} />
        <FormSpy subscription={{ values: true }} onChange={this.handleProcedureBlockSearchChange} />
      </PxGrid>
    );
  };

  rowGetter = ({ index }: any) => {
    const { foundBlockProcedures } = this.state;

    Object.assign(foundBlockProcedures[index], { index: index + 1 });

    return foundBlockProcedures[index];
  };

  foundBlockProceduresRowRenderer = ({ className, columns, index, key, style, rowData }: any) => {

    const ariaProps = { "aria-rowindex": index + 1 };

    return (
      <div
        {...ariaProps}
        className={className}
        key={key}
        role="row"
        style={style}
        onDoubleClick={() => this.upsertBlockProcedure(rowData.id)}
      >
        {columns}
      </div>
    );
  };

  actionsCell = ({ rowData }: any) => {
    const { getMessage } = this.props;

    return (
      <React.Fragment>
        <EditActionButton
          hoverMessage={getMessage("msgEdit")}
          onConfirm={() => this.upsertBlockProcedure(rowData.id)}
        />
        <DeleteActionButton
          hoverMessage={getMessage("msgDelete")}
          popupMessage={getMessage("procedureBlocks.confirmDelete")}
          onConfirm={() => this.onDeleteBlockProcedure(rowData)}
        />
      </React.Fragment>
    );

  };

  renderBlockProceduresSegment = (): React.ReactNode => {

    const { foundBlockProcedures } = this.state;
    const { getMessage } = this.props;
    return (
      <VirtualizedTable
        rowCount={foundBlockProcedures.length}
        rowGetter={this.rowGetter}
        rowRenderer={this.foundBlockProceduresRowRenderer}
        rowHeight={42}
        columns={[
          {
            width: 100,
            label: "#",
            dataKey: "index"
          },
          {
            width: 300,
            flexGrow: 1,
            label: (getMessage("description")),
            dataKey: "description"
          },
          {
            width: 100,
            label: (getMessage("actions")),
            dataKey: "id",
            cellRenderer: (this.actionsCell)
          }
        ]}
      />
    );
  };

  renderLoader = (): React.ReactNode => {

    const { getMessage } = this.props;

    return (
      <Dimmer active inverted>
        <Loader inline="centered" size="medium">{getMessage("procedureBlocks.loader")}</Loader>
      </Dimmer>
    );
  };
}

export default withMessages(BlockProceduresView);
